/**
 * ELD record vocabulary (49 CFR 395 Subpart B, Appendix A, Table 6 / 7.20 / 7.22 / 7.23).
 * Wording follows the regulation: "Off Duty", "Sleeper Berth", "Driving", "On Duty (Not Driving)".
 */
import type { Schemas } from '@/api/client';
import type { DutyStatusCode } from '@/api/pending';

export type EldEvent = Schemas['EldEvent'];

export const DUTY_LABEL: Record<DutyStatusCode, string> = {
  OFF: 'Off Duty',
  SB: 'Sleeper Berth',
  D: 'Driving',
  ON: 'On Duty (Not Driving)',
  PC: 'Personal Conveyance',
  YM: 'Yard Move',
};

/** Grid rows in FMCSA order */
export const GRID_ROWS = ['OFF', 'SB', 'D', 'ON'] as const;
export type GridRow = (typeof GRID_ROWS)[number];

export const DUTY_CODE_BY_EVENT_CODE: Record<number, GridRow> = { 1: 'OFF', 2: 'SB', 3: 'D', 4: 'ON' };
export const EVENT_CODE_BY_DUTY: Record<GridRow, number> = { OFF: 1, SB: 2, D: 3, ON: 4 };

export const RECORD_STATUS: Record<number, string> = {
  1: 'Active',
  2: 'Inactive – changed',
  3: 'Inactive – change requested',
  4: 'Inactive – change rejected',
};

export const RECORD_ORIGIN: Record<number, { short: string; long: string }> = {
  1: { short: 'Auto', long: 'Automatically recorded by ELD' },
  2: { short: 'Driver', long: 'Edited or entered by the driver' },
  3: { short: 'Carrier', long: 'Edit requested by an authenticated user other than the driver' },
  4: { short: 'Assumed', long: 'Assumed from unidentified driver profile' },
};

export const MALFUNCTION_CODES: Record<string, string> = {
  P: 'Power compliance malfunction',
  E: 'Engine synchronization compliance malfunction',
  T: 'Timing compliance malfunction',
  L: 'Positioning compliance malfunction',
  R: 'Data recording compliance malfunction',
  S: 'Data transfer compliance malfunction',
  O: 'Other ELD detected malfunction',
  '1': 'Power data diagnostic event',
  '2': 'Engine synchronization data diagnostic event',
  '3': 'Missing required data elements data diagnostic event',
  '4': 'Data transfer data diagnostic event',
  '5': 'Unidentified driving records data diagnostic event',
  '6': 'Other ELD identified diagnostic event',
};

/** Human description of an event type/code pair. */
export function describeEvent(type: number, code: number, mdCode?: string | null): string {
  switch (type) {
    case 1:
      return DUTY_LABEL[DUTY_CODE_BY_EVENT_CODE[code] ?? 'OFF'] ?? `Duty status ${code}`;
    case 2:
      return code === 2 ? 'Intermediate log (reduced location precision)' : 'Intermediate log';
    case 3:
      return code === 1 ? 'Personal Conveyance' : code === 2 ? 'Yard Move' : 'PC / YM cleared';
    case 4:
      return 'Certification of records';
    case 5:
      return code === 1 ? 'Login' : 'Logout';
    case 6:
      return (
        { 1: 'Engine power-up', 2: 'Engine power-up (reduced precision)', 3: 'Engine shut-down', 4: 'Engine shut-down (reduced precision)' }[
          code
        ] ?? 'Engine power event'
      );
    case 7: {
      const what = mdCode ? (MALFUNCTION_CODES[mdCode] ?? `Code ${mdCode}`) : 'Malfunction/diagnostic';
      return { 1: `${what} logged`, 2: `${what} cleared`, 3: `${what} logged`, 4: `${what} cleared` }[code] ?? what;
    }
    default:
      return `Event type ${type} code ${code}`;
  }
}

export function isDutyStatus(e: Pick<EldEvent, 'eventType'>) {
  return e.eventType === 1;
}

export function isAutoDriving(e: Pick<EldEvent, 'eventType' | 'eventCode' | 'recordOrigin'>) {
  return e.eventType === 1 && e.eventCode === 3 && e.recordOrigin === 1;
}

export function driverName(d: { firstName: string; lastName: string } | null | undefined) {
  return d ? `${d.firstName} ${d.lastName}` : 'Unidentified driver';
}

export const CYCLE_LABEL: Record<string, string> = {
  US_60_7: '60 hours / 7 days',
  US_70_8: '70 hours / 8 days',
};

/**
 * Annotation rule (Appendix A 4.3.2.6 / 7.6): 4–60 characters when the annotation is required and prompted (every
 * annotation the portal asks for: edit proposals, unidentified-driving assignments, trailer / shipping changes,
 * 49 CFR 395.30(c)(2)); 0–60 when optionally entered.
 */
export const ANNOTATION_MIN = 4;
export const ANNOTATION_MAX = 60;
